import * as CubiqTS from "shared/cubiq-model-multi-asset";
import LoggerService from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { ActionButtons } from "app/bfx-execution-monitor/bfx-execution-monitor.const";
import { BfxExecutionMonitorContextMenuService } from "app/bfx-execution-monitor/services/bfx-execution-monitor-context-menu.service";
import {
  BfxBlotterAction,
  BfxBlotterOrdersContextMenuItem,
  COLUMN_ORDER_STATE,
  CcyLookupService,
  CcyPairLookupService,
  OrderPauseState,
} from "shared/cubiq-services";
import { OrderStateHelper } from "shared/cubiq-services/model/orders/OrderStateHelper";
import { ZERO } from "shared/base-components/number-spinner.component";
import { IOrderEntryEvent } from "shared/cubiq-services/model/core/IOrderEntryEvent";
import { CALLOUT_TYPE, IEMActionCallout } from "app/bfx-element-library/models/CalloutModel";

const logger = LoggerService.for(__dirname);

export const EMPTY_AMOUNT_STR = "-";
export class OrderExecutionModel {
  public intent: CubiqTS.OrderIntent;
  public totalAmountStr: string;
  public totalExecutedStr: string;
  public totalRemainingStr: string;

  public vwapPrePips: string;
  public vwapPips: string;
  public vwapPostPips: string;

  public orderStateStr: string;
  public orderState: CubiqTS.OrderState;

  public panelIntent: string;

  public percentFilled: number;

  private orderStateHelper: OrderStateHelper;
  private lastCcy: string = "";
  private lastPrecision: number = 2;

  private orderData: CubiqTS.OrderDataImpl;

  /**  */
  public pauseOperation: CubiqTS.PauseResumeAction = null;

  private _actionCallout: IEMActionCallout;

  public buttonEnabled: ActionButtons<boolean> = {
    modify: true,
    pause: true,
    resume: true,
    fillNow: true,
    cancel: true,
  };
  public buttonVisible: ActionButtons<boolean> = {
    modify: true,
    pause: true,
    resume: true,
    fillNow: true,
    cancel: true,
  };

  /** Number of subscriptions for this model - model can be deleted when 0 */
  private subscriberCount: number = 0;

  public pauseResumeOperationActive: boolean;

  // These flags typically set at compile time
  /** Should Success callouts be visible? */
  public showCalloutSuccess: boolean = false;
  /** Should info/neutral callouts be visible? */
  public showCalloutStatus: boolean = false;
  /** Should warning/negative callouts be visible? */
  public showCalloutWarning: boolean = true;

  /** Is the order actively mobitored inside the UI - or is it Filled/Pending? */
  private locallyMonitored: boolean;

  constructor(
    public readonly orderId: string,
    /** Use to look up VWAP price precisions */
    private ccyPairService: CcyPairLookupService,
    /** Used to look up amount precisions */
    private ccyLookupService: CcyLookupService,
    translateService: TranslateService,
    private menuService: BfxExecutionMonitorContextMenuService,
  ) {
    this.orderStateHelper = new OrderStateHelper(COLUMN_ORDER_STATE, translateService);
    this.reset();
  }

  /**
   * Refresh the OrderExecutionModel with a new OrderDataImpl - all models values can
   * be refreshed here
   * @param order
   * @param locallyMonitored Is the order monitored inside the UI?
   */
  public setOrder(order: CubiqTS.OrderDataImpl, locallyMonitored: boolean) {
    if (!order) {
      logger.out.info(`Not processing update for order (${this.orderId} due to undefined input`);
      return;
    }
    this.locallyMonitored = locallyMonitored;

    this.intent = order.clientIntent;

    this.updateVwapLabels(order);

    const executedQty = order.executedQty ?? ZERO;

    if (order.requestedOrderType === CubiqTS.RequestOrderType.CALL) {
      this.totalAmountStr = "-";
      this.totalExecutedStr = "-";
      this.totalRemainingStr = "-";
      this.percentFilled = 0;
    } else {
      this.totalAmountStr = this.formatCcyAmount(order.fixedCode, order.orderQty);
      this.totalExecutedStr = this.formatCcyAmount(order.fixedCode, executedQty);
      this.totalRemainingStr = this.formatCcyAmount(order.fixedCode, order.leavesQty);
      this.percentFilled = executedQty.dividedBy(order.orderQty).multipliedBy(100).toNumber();
    }

    this.orderState = order.orderState;
    this.orderStateStr = this.orderStateHelper.getState({ data: order });

    this.orderData = order;
    this.updateMenus();

    this.panelIntent = this.getPanelStyleClass();
  }

  public get order(): CubiqTS.OrderData {
    return this.orderData;
  }

  public updateFromTerminalData(event: IOrderEntryEvent) {
    const cloneOrder = new CubiqTS.OrderDataImpl();
    cloneOrder.copyFrom(this.orderData);
    cloneOrder.orderState = event.state;
    if (event.txTime) {
      cloneOrder.txTime = event.txTime;
    }
    cloneOrder.executedQty = event.executedQty;
    cloneOrder.vwapAllInRt = event.vwapAllInRt;
    this.setOrder(cloneOrder, this.locallyMonitored);
  }

  /**
   * Copy price part into 3 different VWAP labels for formatting
   * @param order
   */
  private updateVwapLabels(order: CubiqTS.OrderDataImpl) {
    let vwapProcessed = false;
    if (order.executedQty && !order.executedQty.isZero() && order.vwapAllInRt) {
      const ccyPair = this.ccyPairService.getCcyPair(order.baseCode, order.nonBaseCode, true);
      if (ccyPair) {
        const market = ccyPair.base == order.baseCode;
        const pipSize = market ? ccyPair.rateDecimalsQty : ccyPair.spotInvertDecimalsQty;
        const decimals = order.vwapAllInRt.decimalPlaces();
        const minPrecision = Math.max(decimals, pipSize);
        const vwapStr = order.vwapAllInRt.toFixed(minPrecision);

        // e.g. 1.12345 where pipSize = 4 should give 1.12, 34, 5
        // Start location is the leading digits
        const pipStartLocn = vwapStr.length - 1 - minPrecision + pipSize - 1;
        this.vwapPrePips = vwapStr.substring(0, pipStartLocn);
        this.vwapPips = vwapStr.substring(pipStartLocn, pipStartLocn + 2);
        this.vwapPostPips = vwapStr.substring(pipStartLocn + 2);
        vwapProcessed = true;
      }
    }
    if (!vwapProcessed) {
      this.vwapPrePips = "-";
      this.vwapPips = "";
      this.vwapPostPips = "";
    }
  }

  /**
   * Always ensure menu actions are consistent with the state of the order. We
   * retrieve the new set of context menu states and set our button states accordingly
   * @param order
   */
  private updateMenus() {
    if (!this.orderData) {
      logger.out.warn("Cannot update menus when no order is defined");
      return;
    }
    const menusResponse = this.menuService.getMenuItemsForOrder(this.orderData);

    menusResponse.menuItems.forEach((menuItem: BfxBlotterOrdersContextMenuItem) => {
      switch (menuItem.actionId) {
        case BfxBlotterAction.ACTION_ORDER_FILL_NOW:
          this.buttonEnabled.fillNow = menuItem.isEnabled;
          this.buttonVisible.fillNow = menuItem.isAvailable;
          break;
        case BfxBlotterAction.ACTION_ORDER_PAUSE:
          this.buttonEnabled.pause = menuItem.isEnabled && !this.pauseResumeOperationActive;
          break;
        case BfxBlotterAction.ACTION_ORDER_RESUME:
          this.buttonEnabled.resume = menuItem.isEnabled && !this.pauseResumeOperationActive;
          break;
        case BfxBlotterAction.ACTION_ORDER_MODIFY:
          this.buttonEnabled.modify = menuItem.isEnabled && this.locallyMonitored;
          this.buttonVisible.modify = menuItem.isAvailable;
          break;
        case BfxBlotterAction.ACTION_ORDER_CANCEL:
          this.buttonEnabled.cancel = menuItem.isEnabled;
          this.buttonVisible.cancel = menuItem.isAvailable;
          break;
      }
    });
    if (this.orderData.orderType === CubiqTS.OrderType.ALGO) {
      // For Pause/Resume - we hide one if the other is enabled
      this.buttonVisible.resume = OrderPauseState.isPausedOrderState(this.orderData);
      this.buttonVisible.pause = !this.buttonVisible.resume;
    } else {
      this.buttonVisible.pause = false;
      this.buttonVisible.resume = false;
    }
  }

  /**
   * If we ever lose visibility of the order then we must clear the contents as they
   * are no longer visible or up-to-date
   */
  public reset() {
    this.intent = null;
    this.totalAmountStr = "";
    this.totalExecutedStr = "";
    this.totalRemainingStr = "";

    this.vwapPrePips = "";
    this.vwapPips = "";
    this.vwapPostPips = "";

    this.orderStateStr = "";

    this.percentFilled = 0;
  }

  /**
   * Override to ensure that the top-level blotter also shows full amount precision
   * @param ccy
   * @param params
   * @returns
   */
  protected formatCcyAmount(ccy: string, value: CubiqTS.BigNumber): string {
    const dp = ccy == this.lastCcy ? this.lastPrecision : this.getCcyAmountPrecision(ccy);

    if (value != null && !value.eq(0)) {
      return value.toFormat(dp);
    }

    return EMPTY_AMOUNT_STR;
  }

  /**
   * Get the minor units precision for the specified currency ID
   * @param currencyId
   * @returns
   */
  protected getCcyAmountPrecision(currencyId: string): number {
    const ccyModel = this.ccyLookupService.getCcy(currencyId);
    let precision: number = 0;

    if (ccyModel != null && ccyModel.minorUnitsQty != null) {
      precision = ccyModel.minorUnitsQty.toNumber();
    }
    this.lastCcy = currencyId;
    this.lastPrecision = precision;

    return precision;
  }

  public getSubscriberCount(): number {
    return this.subscriberCount;
  }

  /**
   * Used to track new subscribers
   */
  public addSubscriber() {
    this.subscriberCount++;
  }
  /**
   * Used to track new subscribers
   */
  public removeSubscriber() {
    this.subscriberCount--;
  }

  private getPanelStyleClass(): CALLOUT_TYPE {
    if (!this.orderData) {
      logger.out.warn("Cannot update panel styles when no order is defined");
      return;
    }
    let calloutType: CALLOUT_TYPE;
    switch (this.orderData.orderState) {
      case CubiqTS.OrderState.FILLED:
        calloutType = "positive";
        break;

      case CubiqTS.OrderState.CANCELLED:
      case CubiqTS.OrderState.EXPIRED:
      case CubiqTS.OrderState.FILL_ERROR:
      case CubiqTS.OrderState.FILL_EXPIRED:
      case CubiqTS.OrderState.REJECTED:
        calloutType = "negative";
        break;
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.PENDING:
        calloutType = "neutral";
        break;
      case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.CANCEL_PENDING_FWDS:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.EXPIRY_PENDING_FWDS:
      case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.PENDING_CANCELLATION:
      case CubiqTS.OrderState.PENDING_FWDS:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        calloutType = "info";
        break;
      default:
        if (OrderPauseState.isPausedOrderState(this.orderData)) {
          calloutType = "neutral";
        } else {
          calloutType = "positive";
        }
        break;
    }
    return calloutType;
  }

  /**
   * Change the pause/resume operation status of the order
   * @param active
   */
  public setPauseResumeOperationActive(active: boolean) {
    if (this.pauseResumeOperationActive !== active) {
      this.pauseResumeOperationActive = active;
      this.updateMenus();
    }
  }

  /**
   * Define the current active callout - this will be displayed
   * only if passing the visibility constraints
   * @param value
   * @returns true if the visible callout has changed
   */
  public setActionCallout(value: IEMActionCallout): boolean {
    const oldCallout = this.getVisibleActionCallout();
    this._actionCallout = value;
    const newCallout = this.getVisibleActionCallout();
    if (!oldCallout || !newCallout) {
      // chnage if one if null but not both
      return oldCallout !== newCallout;
    }
    const changed =
      oldCallout.calloutType !== newCallout.calloutType ||
      oldCallout.calloutMessage !== newCallout.calloutMessage ||
      oldCallout.calloutTooltip !== newCallout.calloutTooltip;
    if (changed) {
      logger.out.info(
        `Updating callout for order ${this.orderId} - ${value ? value.toString() : "<NULL>"}`,
      );
    }
    return changed;
  }

  public hasActionCallout(): boolean {
    return this._actionCallout != null;
  }

  /**
   * Will return true if a callout is defined and the calloutType is confdigured as visible
   */
  public hasVisibleActionCallout(): boolean {
    let calloutVisible: boolean = false;
    if (this._actionCallout) {
      switch (this._actionCallout.calloutType) {
        case "neutral":
        case "info":
          calloutVisible = this.showCalloutStatus;
          break;
        case "positive":
          calloutVisible = this.showCalloutSuccess;
          break;
        case "warning":
        case "negative":
        default:
          calloutVisible = this.showCalloutWarning;
          break;
      }
      return calloutVisible;
    }
    return false;
  }

  /**
   * Will return a callout based on the 3 visibility poperties of the model. These will be
   * set up at creation time, typically
   */
  public getVisibleActionCallout(): IEMActionCallout {
    let calloutVisible: boolean = this.hasVisibleActionCallout();
    return calloutVisible ? this._actionCallout : null;
  }
}
